<template>
    <div>
        <group-users-controls
            v-model="fetchParams"
            @apply="applyFilters"
            @reset="resetFilters"
            :sortOptions="[
                {
                    value: 'added_at',
                    label: 'Sort: Added at',
                },
            ]"
            :roleOptions="groupRolesOptions()"
            :groupId="props.groupId"
            :loading="loading"
            @add="openUserGroupModal()"
        >
        </group-users-controls>

        <page-loading v-if="loading && !list.length"></page-loading>

        <template v-if="!loading && !list.length">
            <empty-state v-if="!isEqual(defaultFilters(), fetchParams)" @reset="resetFilters" />
            <empty-state v-else-if="!list.length">
                No users yet.
                <vx-button v-if="onCorporate() || ['admin', 'owner'].includes(roleOnCurrentGroup)" @click="openUserGroupModal()">
                    Add User <font-awesome-icon widthAuto :icon="faCirclePlus" />
                </vx-button>
            </empty-state>
        </template>

        <vx-table v-if="list.length" class="xl:grid-cols-4 mt-0" rowBreakpoint="xl">
            <vx-table-row isHeader>
                <div class="xl:col-span-2">Name</div>
                <div>Role</div>
            </vx-table-row>

            <group-active-user-row
                v-for="(item, index) in list"
                v-model="list[index]"
                :key="list[index].id"
                :groupId="props.groupId"
                :teamId="props.teamId"
                @edit="openUserGroupModal(list[index])"
                @deleted="removeFromList(list[index].id)"
            >
            </group-active-user-row>
        </vx-table>

        <div v-if="list.length" class="mt-8 flex flex-col w-full gap-10">
            <div class="flex justify-between items-center">
                <vx-button
                    v-if="fetchParams.cursor"
                    @click="loadData()"
                    color="muted-dark"
                    size="xl"
                    data-test="load-more-btn"
                    class="grow-0"
                    :loading="loading"
                >
                    Load More
                </vx-button>
                <div class="text-lg grow-0 ml-auto font-normal text-slate-600">
                    {{ list.length }}/{{ totalCount }}
                </div>
            </div>
        </div>

        <vx-alert shadow class="mt-8" color="slate" :closeable="false">
            <template v-slot:icon><font-awesome-icon widthAuto :icon="faCircleInfo" /></template>
            Corporate or OU administrators with implicit access will not be duplicated here.
        </vx-alert>

        <group-user-modal-form
            v-model="selectedUser"
            v-model:visible="showUserGroupModal"
            :groupId="props.groupId"
            :groupName="props.groupName"
            :teamId="props.teamId"
            @added="userAdded($event)"
            @updated="userUpdated($event)"
            @invited="emit('invited')"
        ></group-user-modal-form>
    </div>
</template>
<script setup>
import PageLoading from '~/components/general/PageLoading.vue';
import EmptyState from '~/components/general/EmptyState.vue';
import GroupActiveUserRow from './GroupActiveUserRow.vue';
import GroupUsersControls from './GroupUsersControls.vue';
import { groupUsersClient } from '~/services';
import { VxAlert, VxButton, VxTable, VxTableRow } from '@voxie/frontend-components';
import { faCirclePlus, faCircleInfo } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { useCursorPagination } from '~/composables/useCursorPagination';
import { onMounted, ref } from 'vue';
import { isEqual } from '~/utils/helpers';
import { DEFAULT_GROUP_ROLE, groupRolesOptions } from '~/constants/roles';
import { useCurrentGroup } from '~/composables/useCurrentGroup';
import GroupUserModalForm from '~/components/settings/teams/groups/form/users/GroupUserModalForm.vue';

const props = defineProps({
    teamId: {
        type: Number,
        required: true,
    },
    groupId: {
        type: String,
        required: true,
    },
    groupName: {
        type: String,
        required: false,
    },
});
const emit = defineEmits(['add', 'added', 'edit', 'updated', 'invited']);

const defaultFilters = () => ({
    cursor: null,
    sort_dir: 'desc',
    sort_field: 'added_at',
    per_page: 10,
    role: undefined,
    query: null,
});

const totalCount = ref(0);
const showUserGroupModal = ref(false);
const selectedUser = ref({
    id: null,
    name: '',
    email: '',
    role: DEFAULT_GROUP_ROLE,
});
const openUserGroupModal = (user) => {
    if (user) {
        selectedUser.value = { ...user };
    }

    showUserGroupModal.value = true;
};

const { onCorporate, roleOnCurrentGroup } = useCurrentGroup();
const { list, loading, fetchParams, fetchRecords } = useCursorPagination();

const resetFilters = () => {
    fetchParams.value = defaultFilters();

    loadData();
};

const applyFilters = () => {
    fetchParams.value = {
        ...fetchParams.value,
        cursor: null,
    };

    loadData();
};

const loadData = () => {
    fetchRecords(groupUsersClient.listGroupUsers(props.teamId, props.groupId, { ...fetchParams.value }));
    fetchTotalCount();
};

const fetchTotalCount = async () => {
    try {
        const response = await groupUsersClient.countGroupUsers(props.teamId, props.groupId, { ...fetchParams.value });
        totalCount.value = response.data?.count || 0;
    } catch (e) {
        console.error(e);
    }
};

const removeFromList = (itemId) => {
    list.value = list.value.filter((item) => item.id != itemId);
    totalCount.value = totalCount.value - 1;
    if (!list.value.length) {
        loadData();
    }
};

const userAdded = (user) => {
    list.value.unshift(user);
    totalCount.value = totalCount.value + 1;

    emit('added', user);
};

const userUpdated = (updatedUser) => {
    list.value = list.value.map((user) => {
        if (user.id === updatedUser.id) {
            return {
                ...updatedUser,
            };
        } else if (updatedUser.role === 'owner' && user.role === 'owner') {
            return {
                ...user,
                role: 'admin',
            };
        }

        return user;
    });

    emit('updated', updatedUser);
};

onMounted(() => {
    resetFilters();
});

defineExpose({
    loadData,
});
</script>
